// src/modules/customers/components/SavedBankAccountSelect.jsx
//
// Fills a case's bank fields (bankAccountNumber / ifscCode / bankBranchName)
// from the bank accounts saved on the customer. The case keeps its own copy, so
// a later change to the customer never rewrites a past case.

import React, { useEffect, useRef } from 'react';
import { Form, Select, Space, Tag, Typography } from 'antd';
import { BankOutlined } from '@ant-design/icons';

const { Text } = Typography;

export const bankFieldsFrom = (a) => ({
  bankAccountNumber: a?.accountNumber || '',
  ifscCode: a?.ifscCode || '',
  bankBranchName: [a?.bankName, a?.branchName].filter(Boolean).join(' - '),
});

const primaryOf = (customer) => {
  const list = customer?.bankAccounts || [];
  return list.find((a) => a.isPrimary) || list[0] || null;
};

const isBlank = (form) => ['bankAccountNumber', 'ifscCode', 'bankBranchName']
  .every((f) => !String(form.getFieldValue(f) || '').trim());

/**
 * Auto-fill the primary account when a customer is picked.
 *   * Picking a different customer replaces the bank fields (with that
 *     customer's primary account, or blanks — never the previous customer's).
 *   * The first customer seen on an existing case (`keepExisting`) only fills
 *     blank fields, so a saved case keeps the account it was paid to.
 */
export function useCustomerBankAutofill(form, customer, { keepExisting = false, disabled = false } = {}) {
  const lastId = useRef(null);
  useEffect(() => {
    if (disabled || !customer?._id || lastId.current === customer._id) return;
    const first = lastId.current === null;
    lastId.current = customer._id;
    if (first && keepExisting && !isBlank(form)) return;
    const primary = primaryOf(customer);
    if (!primary && isBlank(form)) return;
    form.setFieldsValue(bankFieldsFrom(primary));
  }, [customer, form, keepExisting, disabled]);
}

/** Picker for switching between the customer's saved accounts. */
export default function SavedBankAccountSelect({ form, customer, disabled }) {
  const accounts = customer?.bankAccounts || [];
  const currentNo = Form.useWatch('bankAccountNumber', form);
  const currentIfsc = Form.useWatch('ifscCode', form);
  if (!customer) return null;
  if (!accounts.length) {
    return (
      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
        No bank account saved on this customer — add one on the customer page to auto-fill next time.
      </Text>
    );
  }

  const match = accounts.find((a) => a.accountNumber === currentNo && a.ifscCode === currentIfsc);

  return (
    <div style={{ marginBottom: 12 }}>
      <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>Fill from saved account</Text>
      <Select
        style={{ width: '100%', maxWidth: 520 }}
        placeholder="Choose a saved bank account"
        value={match?._id}
        disabled={disabled}
        onChange={(accId) => form.setFieldsValue(bankFieldsFrom(accounts.find((a) => a._id === accId)))}
        options={accounts.map((a) => ({
          value: a._id,
          label: (
            <Space>
              <BankOutlined />
              <span>{a.bankName || a.ifscCode} ••••{String(a.accountNumber).slice(-4)}</span>
              <Text type="secondary">{a.accountHolderName}</Text>
              {a.isPrimary && <Tag color="gold">Primary</Tag>}
            </Space>
          ),
        }))}
      />
    </div>
  );
}
